"use client";

import React, { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown, Sun, Moon, Monitor, Cpu } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../ThemeContext";

const Navbar = () => {
  const { isDark, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileExpandedCategory, setMobileExpandedCategory] = useState(null);
  const servicesRef = useRef(null);
  const closeTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) =>
    location.pathname === path ? "text-green-400" : "text-white";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setIsServicesOpen(false);
        setExpandedCategory(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (
      !location.pathname.startsWith("/services") &&
      !location.pathname.startsWith("/web-development") &&
      !location.pathname.startsWith("/mobile-app-development") &&
      !location.pathname.startsWith("/ui-ux-design") &&
      !location.pathname.startsWith("/custom-software-development") &&
      !location.pathname.startsWith("/ai-automation") &&
      !location.pathname.startsWith("/engineering")
    ) {
      setIsServicesOpen(false);
      setExpandedCategory(null);
    }
  }, [location.pathname]);

  const handleServicesMouseEnter = () => {
    clearTimeout(closeTimer.current);
    setIsServicesOpen(true);
  };
  const handleServicesMouseLeave = () => {
    closeTimer.current = setTimeout(() => {
      setIsServicesOpen(false);
      setExpandedCategory(null);
    }, 150);
  };

  const toggleCategory = (cat) =>
    setExpandedCategory((prev) => (prev === cat ? null : cat));

  // ✅ Updated: "IT Solutions & Consultancy" → "Custom Software Development"
  const softwareLinks = [
    { label: "Mobile App Development",      path: "/mobile-app-development" },
    { label: "Web Development",             path: "/web-development" },
    { label: "Graphic & UI/UX Design",      path: "/ui-ux-design" },
    { label: "Custom Software Development", path: "/custom-software-development" },
    { label: "AI Automation",               path: "/ai-automation" },
  ];

  const engineeringLinks = [
    { label: "IoT Development",  path: "/engineering/iot" },
    { label: "Embedded Systems", path: "/engineering/embedded-systems" },
    { label: "Robotics",         path: "/engineering/robotics" },
    { label: "PCB Design",       path: "/engineering/pcb-design" },
    { label: "Smart Home",       path: "/engineering/smart-home" },
  ];

  const navBg = isScrolled ? "backdrop-blur-md shadow-lg" : "";

  const ThemeToggle = ({ mobile }) => (
    <button
      onClick={toggleTheme}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      style={{
        backgroundColor: isDark ? "#000000" : "#ffffff",
        border: "1px solid rgba(255,255,255,0.35)",
        transition: "background-color 0.3s ease",
      }}
      className={`relative flex items-center flex-shrink-0 px-1 rounded-full ${mobile ? "w-12 h-6" : "w-14 h-7"}`}
    >
      <span
        style={{
          backgroundColor: isDark ? "#000000" : "#ffffff",
          border: isDark ? "1px solid rgba(255,255,255,0.25)" : "1px solid #e5e7eb",
          transition: "left 0.3s ease, background-color 0.3s ease",
        }}
        className={`absolute rounded-full flex items-center justify-center shadow-sm ${mobile ? "w-4 h-4" : "w-5 h-5"} ${
          isDark ? (mobile ? "left-[26px]" : "left-[30px]") : "left-1"
        }`}
      >
        {isDark
          ? <Moon size={mobile ? 9 : 11} className="text-white" />
          : <Sun  size={mobile ? 9 : 11} className="text-gray-400" />
        }
      </span>
    </button>
  );

  return (
    <nav
      aria-label="Fiieve main navigation"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${navBg} text-white shadow-[0_2px_0_#000] px-4 md:px-8 py-6 md:py-7 flex justify-between items-center`}
      style={{ backgroundColor: "#00321a" }}
    >
      {/* ── Logo ── */}
      <Link
        to="/"
        onClick={() => setIsOpen(false)}
        aria-label="Fiieve — Software & Engineering Company — Home"
        className="-ml-2 md:-ml-8 md:ml-0"
      >
        <img
          src="/Images/logo.png"
          alt="Fiieve — Web Development & AI Automation Company UK"
          width="160"
          height="56"
          className="h-28 md:h-40 w-auto object-contain -my-10 md:-my-14"
          loading="eager"
          fetchpriority="high"
        />
      </Link>

      {/* ── Desktop Menu ── */}
      <ul
        className="hidden md:flex items-center space-x-2 text-base font-medium border border-green-400/60 rounded-full px-6 py-2.5"
        role="menubar"
        aria-label="Primary navigation"
      >
        <li role="none">
          <Link to="/" role="menuitem" aria-label="Home — Fiieve Web Development & AI Automation"
            className={`${isActive("/")} hover:text-green-400 transition-colors px-4 py-1`}>
            Home
          </Link>
        </li>
        <li role="none">
          <Link to="/about-us" role="menuitem" aria-label="About Fiieve — Software & Engineering Company"
            className={`${isActive("/about-us")} hover:text-green-400 transition-colors px-4 py-1`}>
            About Us
          </Link>
        </li>

        {/* ── Services Dropdown ── */}
        <li ref={servicesRef} className="relative" role="none"
          onMouseEnter={handleServicesMouseEnter} onMouseLeave={handleServicesMouseLeave}>
          <div className="flex items-center gap-1 px-4 py-1">
            <span role="menuitem" aria-haspopup="true" aria-expanded={isServicesOpen}
              aria-label="Services — Web Development, Mobile Apps, AI Automation, UI/UX Design"
              className={`${isActive("/services")} hover:text-green-400 transition-colors cursor-pointer`}
              onClick={() => navigate("/services")}>
              Services
            </span>
            <button onClick={(e) => { e.stopPropagation(); setIsServicesOpen((p) => !p); }}
              aria-label={isServicesOpen ? "Close services menu" : "Open services menu"}
              aria-expanded={isServicesOpen} className="focus:outline-none cursor-pointer">
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? "rotate-180" : ""}`} />
            </button>
          </div>

          {isServicesOpen && <div className="absolute top-full left-0 w-full h-3" aria-hidden="true" />}

          {isServicesOpen && (
            <div role="menu" aria-label="Services submenu"
              className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 bg-black border border-green-400/40 rounded-2xl w-72 shadow-xl z-50 overflow-hidden py-2">
              <button role="menuitem" aria-haspopup="true" aria-expanded={expandedCategory === "software"}
                aria-label="Software Services — Web, Mobile, AI, UI/UX"
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/5 transition-colors group"
                onClick={() => toggleCategory("software")}>
                <span className="flex items-center gap-2 text-sm font-semibold text-white group-hover:text-green-400 transition-colors">
                  <Monitor size={15} className="text-green-400" aria-hidden="true" />
                  Software Services
                </span>
                <ChevronDown size={14} aria-hidden="true"
                  className={`transition-transform duration-200 ${expandedCategory === "software" ? "rotate-180 text-green-400" : "text-white/40"}`} />
              </button>

              {expandedCategory === "software" && (
                <ul role="menu" aria-label="Software services list" className="border-t border-white/10 bg-white/5 pb-1">
                  {softwareLinks.map((s) => (
                    <li key={s.label} role="none">
                      <Link to={s.path} role="menuitem"
                        className="block px-8 py-2.5 text-sm text-white/80 hover:text-green-400 hover:bg-white/5 transition-all"
                        onClick={() => { setIsServicesOpen(false); setExpandedCategory(null); }}>
                        {s.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mx-4 border-t border-white/10" aria-hidden="true" />

              <button role="menuitem" aria-haspopup="true" aria-expanded={expandedCategory === "engineering"}
                aria-label="Engineering Services — IoT, Embedded Systems, Robotics, PCB, Smart Home"
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/5 transition-colors group"
                onClick={() => toggleCategory("engineering")}>
                <span className="flex items-center gap-2 text-sm font-semibold text-white group-hover:text-green-400 transition-colors">
                  <Cpu size={15} className="text-green-400" aria-hidden="true" />
                  Engineering Services
                </span>
                <ChevronDown size={14} aria-hidden="true"
                  className={`transition-transform duration-200 ${expandedCategory === "engineering" ? "rotate-180 text-green-400" : "text-white/40"}`} />
              </button>

              {expandedCategory === "engineering" && (
                <ul role="menu" aria-label="Engineering services list" className="border-t border-white/10 bg-white/5 pb-1">
                  {engineeringLinks.map((s) => (
                    <li key={s.label} role="none">
                      <Link to={s.path} role="menuitem"
                        className="block px-8 py-2.5 text-sm text-white/80 hover:text-green-400 hover:bg-white/5 transition-all"
                        onClick={() => { setIsServicesOpen(false); setExpandedCategory(null); }}>
                        {s.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </li>

        <li role="none">
          <Link to="/portfolio" role="menuitem" aria-label="Portfolio — Fiieve Web & App Projects"
            className={`${isActive("/portfolio")} hover:text-green-400 transition-colors px-4 py-1`}>
            Portfolio
          </Link>
        </li>
        <li role="none">
          <Link to="/portal" role="menuitem" aria-label="Client Portal — Fiieve Project Management"
            className={`${isActive("/portal")} hover:text-green-400 transition-colors px-4 py-1`}>
            Portal
          </Link>
        </li>
        <li role="none">
          <Link to="/careers" role="menuitem" aria-label="Careers at Fiieve — Join Our Team"
            className={`${isActive("/careers")} hover:text-green-400 transition-colors px-4 py-1`}>
            Careers
          </Link>
        </li>
      </ul>

      {/* ── Desktop Right ── */}
      <div className="hidden md:flex items-center gap-3">
        <ThemeToggle />
        <Link to="/contact-us" aria-label="Contact Fiieve — Get a Free Quote"
          className="bg-orange-400 text-black px-6 py-2.5 rounded-full text-base font-semibold hover:bg-orange-300 transition-all">
          Contact Us
        </Link>
      </div>

      {/* ── Mobile Right ── */}
      <div className="md:hidden flex items-center gap-2 z-50">
        <ThemeToggle mobile />
        {isOpen
          ? <X className="w-6 h-6 cursor-pointer" onClick={() => setIsOpen(false)} aria-label="Close navigation menu" role="button" />
          : <Menu className="w-6 h-6 cursor-pointer" onClick={() => setIsOpen(true)} aria-label="Open navigation menu" role="button" />
        }
      </div>

      {/* ── Mobile Menu ── */}
      {isOpen && (
        <div className="absolute left-0 w-full md:hidden z-40 border-t border-white/10 overflow-y-auto"
          style={{ backgroundColor: "#00321a", top: "100%", maxHeight: "calc(100vh - 60px)" }}
          role="dialog" aria-label="Mobile navigation menu">
          <ul className="py-3 border-b border-white/10" role="menu">
            {[
              { path: "/",          label: "Home",      aria: "Home — Fiieve" },
              { path: "/about-us",  label: "About Us",  aria: "About Fiieve" },
              { path: "/portfolio", label: "Portfolio", aria: "Fiieve Portfolio" },
              { path: "/portal",    label: "Portal",    aria: "Client Portal — Fiieve" },
              { path: "/careers",   label: "Careers",   aria: "Careers at Fiieve" },
            ].map(({ path, label, aria }) => (
              <li key={path} role="none">
                <Link to={path} role="menuitem" aria-label={aria}
                  className={`block text-center py-2.5 text-base font-semibold ${isActive(path)} hover:text-green-400 hover:bg-white/5 transition-all`}
                  onClick={() => setIsOpen(false)}>
                  {label}
                </Link>
              </li>
            ))}

            <li role="none">
              <button role="menuitem" aria-haspopup="true" aria-expanded={mobileServicesOpen}
                aria-label="Services — expand to see all Fiieve services"
                className="w-full flex items-center justify-center gap-2 py-2.5 text-base font-semibold text-white hover:text-green-400 hover:bg-white/5 transition-all"
                onClick={() => { setMobileServicesOpen((p) => !p); setMobileExpandedCategory(null); }}>
                Services
                <ChevronDown aria-hidden="true"
                  className={`w-4 h-4 transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>
            </li>
          </ul>

          {mobileServicesOpen && (
            <div className="border-b border-white/10" role="menu" aria-label="Mobile services submenu">
              <button role="menuitem" aria-haspopup="true" aria-expanded={mobileExpandedCategory === "software"}
                aria-label="Software Services"
                className="w-full flex items-center justify-between px-6 py-3 hover:bg-white/5 transition-colors"
                onClick={() => setMobileExpandedCategory((p) => p === "software" ? null : "software")}>
                <span className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Monitor size={14} className="text-green-400" aria-hidden="true" />
                  Software Services
                </span>
                <ChevronDown size={13} aria-hidden="true"
                  className={`text-white/40 transition-transform duration-200 ${mobileExpandedCategory === "software" ? "rotate-180" : ""}`} />
              </button>
              {mobileExpandedCategory === "software" && (
                <ul role="menu" aria-label="Software services" className="bg-white/5 border-t border-white/10 pb-1">
                  {softwareLinks.map((s) => (
                    <li key={s.label} role="none">
                      <Link to={s.path} role="menuitem"
                        className="block text-center py-2 text-sm text-white/70 hover:text-green-400 hover:bg-white/5 transition-all"
                        onClick={() => setIsOpen(false)}>
                        {s.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mx-4 border-t border-white/10" aria-hidden="true" />

              <button role="menuitem" aria-haspopup="true" aria-expanded={mobileExpandedCategory === "engineering"}
                aria-label="Engineering Services"
                className="w-full flex items-center justify-between px-6 py-3 hover:bg-white/5 transition-colors"
                onClick={() => setMobileExpandedCategory((p) => p === "engineering" ? null : "engineering")}>
                <span className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Cpu size={14} className="text-green-400" aria-hidden="true" />
                  Engineering Services
                </span>
                <ChevronDown size={13} aria-hidden="true"
                  className={`text-white/40 transition-transform duration-200 ${mobileExpandedCategory === "engineering" ? "rotate-180" : ""}`} />
              </button>
              {mobileExpandedCategory === "engineering" && (
                <ul role="menu" aria-label="Engineering services" className="bg-white/5 border-t border-white/10 pb-1">
                  {engineeringLinks.map((s) => (
                    <li key={s.label} role="none">
                      <Link to={s.path} role="menuitem"
                        className="block text-center py-2 text-sm text-white/70 hover:text-green-400 hover:bg-white/5 transition-all"
                        onClick={() => setIsOpen(false)}>
                        {s.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="py-4 text-center">
            <Link to="/contact-us" aria-label="Contact Fiieve — Get a Free Quote Today"
              className="bg-orange-500 text-white px-7 py-2.5 rounded-full text-sm font-semibold hover:bg-orange-400 transition-all inline-block"
              onClick={() => setIsOpen(false)}>
              Contact Us
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;